/**
 * The layout vocabulary, in one place.
 *
 * The proportions here are the single source the Builder uses for BOTH the
 * miniature previews in the layout chooser AND the real grid on the canvas.
 * That is deliberate: if the chooser drew its thumbnails from a separate
 * table, a layout could advertise "1/4 + 3/4" in the picker and render as
 * something else the moment it was applied, and nobody would notice until a
 * user did.
 *
 * The numbers are relative parts, matching `design_schemas.ColumnLayout` and
 * `COLUMN_TEMPLATES` in the Website Module. `gridTemplate()` below produces
 * the same `minmax(0, Nfr)` tracks the renderer uses, so the canvas grid and
 * the published grid are computed identically rather than merely similarly.
 */
import type { ColumnLayout } from './types';

export interface LayoutDefinition {
  id: ColumnLayout;
  label: string;
  /** Relative width of each column. Length is the column count. */
  parts: number[];
}

export const LAYOUTS: LayoutDefinition[] = [
  { id: '1', label: '1 kolom', parts: [1] },
  { id: '2', label: '2 gelijke kolommen', parts: [1, 1] },
  { id: '1-2', label: 'Smal + breed', parts: [1, 2] },
  { id: '2-1', label: 'Breed + smal', parts: [2, 1] },
  { id: '1-3', label: 'Kwart + driekwart', parts: [1, 3] },
  { id: '3-1', label: 'Driekwart + kwart', parts: [3, 1] },
  { id: '3', label: '3 gelijke kolommen', parts: [1, 1, 1] },
  { id: '4', label: '4 gelijke kolommen', parts: [1, 1, 1, 1] },
  { id: '1-1-2', label: 'Twee smal + breed', parts: [1, 1, 2] },
  { id: '2-1-1', label: 'Breed + twee smal', parts: [2, 1, 1] },
];

const BY_ID = new Map(LAYOUTS.map((layout) => [layout.id, layout]));

export function layoutDefinition(id: ColumnLayout | undefined): LayoutDefinition {
  return BY_ID.get(id ?? '1') ?? LAYOUTS[0]!;
}

/**
 * The desktop grid for a layout, in the renderer's own form.
 *
 * `minmax(0, Nfr)` rather than plain `Nfr`: a grid track's default minimum is
 * `auto`, so a long unbroken word or a wide image inflates its track past its
 * share and pushes the row into horizontal overflow.
 */
export function gridTemplate(id: ColumnLayout | undefined): string {
  return layoutDefinition(id).parts.map((part) => `minmax(0, ${part}fr)`).join(' ');
}

/**
 * Infer the layout of a section that predates `columnLayout`.
 *
 * Legacy V2 sections stored only their columns, so the count is the only
 * signal available. Equal-width is the honest reading: nothing recorded that
 * the author wanted them uneven.
 */
export function inferLayout(columnCount: number): ColumnLayout {
  const byCount: Record<number, ColumnLayout> = { 1: '1', 2: '2', 3: '3', 4: '4' };
  return byCount[columnCount] ?? '1';
}
